<script setup>

import NumberOnlyInput from "@/components/common/input/NumberOnlyInput"
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown"
import Button from "@/components/common/input/Button"
import MovingTooltip from "@/components/common/other/MovingTooltip"
import DimensionExtended from "@/classes/component-classes/DimensionExtended";
import Dimension from "@/classes/component-classes/Dimension";

import {useGetValidatedFields} from "@/composables/common/useGetValidatedFields";

import {ref, onMounted, nextTick, inject, computed} from 'vue';
import {useStore} from "vuex";

const store = useStore();
const localDimsArray = ref([]);
const piecesInputRef = ref();

let weightTotal = computed(() => {

  let sumWeightColumn = 0;

  for (let i = 0; i < props.modelValue.length; i++) {

    sumWeightColumn +=  props.modelValue[i].weight
  }

  return sumWeightColumn.toString();
})

const emits = defineEmits(['entry-update'])

const props = defineProps({

  modelValue: {
    type: Array, default: function () {
      return []
    },
  },

  requiredFields: {type: Array, default: []},
  validationFail: Boolean,
  packaging: {type: Array, default: []},

})


function addDimension(dim, index) {

  let valid = validate(dim);

  if (valid) {

    dim.showAddButton = false;
    dim.readOnly = true;

    localDimsArray.value.push(new DimensionExtended());
    props.modelValue.push(new Dimension());

    roundUpDimensions(dim);

    modelValueUpdate(dim, index);

    nextTick(function () {

      // Set focus on the pieces input for a new line
      piecesInputRef.value[index + 1].$el.firstChild.getElementsByClassName('input-main-style')[0].focus();
    });

  }

}

function roundUpDimensions(dim) {

  dim.pieces = parseInt(dim.pieces);
  dim.length = Math.ceil(dim.length);
  dim.width = Math.ceil(dim.width);
  dim.height = Math.ceil(dim.height);
  dim.weight = Math.ceil(dim.weight);

}

function validate(dim) {

  let valid = true;

  if(!dim.pieces || !dim.length || !dim.width || !dim.height) {

    return valid = false;

  }

  // This code will validate the individual fields according to the custom validation object passed in if the above code is removed
  props.requiredFields.forEach(item => {

    if (item.validate) {

      if (typeof (dim[item.modelName]) === 'object' && dim.hasOwnProperty(item.modelName.toString())) {

        if (!dim[item.modelName].text) {

          dim.validation[item.modelName].value = true;

          valid = false;

        }

      } else {

        if (dim.hasOwnProperty(item.modelName.toString())) {

          if (!dim[item.modelName]) {

            dim.validation[item.modelName].value = true;

            valid = false;

          }
        }
      }
    } else {

      // if(!dim.pieces || !dim.length || !dim.width || !dim.height) {
      //
      //   valid = false;
      //
      // }

    }
  })

  return valid

}

function modelValueUpdate(dim, index) {

  // Collect the pure model keys and update the values from the current local dim
  for (const propName in props.modelValue[0]) {
    props.modelValue[index][propName] = dim[propName]
  }

  emits('entry-update', weightTotal)

}

function prepareDelete(dim) {
  dim.isDeleting = true;
}

function cancelDelete(dim) {
  dim.isDeleting = false;
}

function removeDimension(index, dim) {

  localDimsArray.value.splice(index, 1);
  props.modelValue.splice(index, 1);

  localDimsArray.value[localDimsArray.value.length - 1].isEditing = false;
  localDimsArray.value[localDimsArray.value.length - 1].readOnly = false;

  emits('entry-update', weightTotal)
}

function editDimension(dim) {
  localDimsArray.value.forEach(item => {
    item.readOnly = true;
  });

  dim.readOnly = false;
  dim.isEditing = true;

  // Take the add button away if editing a line
  localDimsArray.value[localDimsArray.value.length - 1].isEditing = true;

}

function acceptEdit(dim, index) {

  let valid = validate(dim)

  if (valid) {

    if (index === localDimsArray.value.length - 1) {
      props.modelValue.push(new Dimension())
    }

    roundUpDimensions(dim);

    modelValueUpdate(dim, index);

    dim.readOnly = true;
    localDimsArray.value[localDimsArray.value.length - 1].isEditing = false;
    localDimsArray.value[localDimsArray.value.length - 1].readOnly = false;
  }

}

onMounted(() => {

  // Set memory reference from local data
  let dimensions = localDimsArray.value

  props.modelValue.forEach(function (item) {

    // Cast the pure model class into the local extended class
    let dimension = {...new DimensionExtended(), ...item}

    // Set all correct props for returning data for button actions
    dimension.showAddButton = false;
    dimension.readOnly = true;

    dimensions.push(dimension)
  })

  // Add a new empty line of dims after the current dims
  dimensions.push(new DimensionExtended())

})

</script>

<template>
  <div class="cmp-dimentions-entry" style="width: 920px">
    <div class="cmp-dimentions-entry_dims-header">
      <div :class="{'global-required-field': useGetValidatedFields(requiredFields, 'pieces')}">Pieces</div>
      <div :class="{'global-required-field': useGetValidatedFields(requiredFields, 'length')}">Length</div>
      <div :class="{'global-required-field': useGetValidatedFields(requiredFields, 'width')}">Width</div>
      <div :class="{'global-required-field': useGetValidatedFields(requiredFields, 'height')}">Height</div>
      <div :class="{'global-required-field': useGetValidatedFields(requiredFields, 'weight')}">Weight</div>
      <div :class="{'global-required-field': useGetValidatedFields(requiredFields, 'packaging')}">Packaging</div>
    </div>
    <div class="cmp-dimentions-entry_container">
      <div v-for="(dim, index) in localDimsArray" class="cmp-dimentions-entry_wrapper" :key="index">
        <NumberOnlyInput
            ref="piecesInputRef"
            :maxLength="4"
            v-model="dim.pieces"
            v-model:validation-fail="dim.validation.pieces"
            :readonly="dim.readOnly"
            :width="56">
        </NumberOnlyInput>
        <NumberOnlyInput
            v-model="dim.length"
            v-model:validation-fail="dim.validation.length"
            :allowDecimal="true"
            :readonly="dim.readOnly"
            :width="100">
        </NumberOnlyInput>
        <NumberOnlyInput
            v-model="dim.width"
            v-model:validation-fail="dim.validation.width"
            :allowDecimal="true"
            :readonly="dim.readOnly"
            :width="100">
        </NumberOnlyInput>
        <NumberOnlyInput
            v-model="dim.height"
            v-model:validation-fail="dim.validation.height"
            :allowDecimal="true"
            :readonly="dim.readOnly"
            :width="100">
        </NumberOnlyInput>
        <NumberOnlyInput
            v-model="dim.weight"
            v-model:validation-fail="dim.validation.weight"
            :allowDecimal="true"
            :readonly="dim.readOnly"
            :width="100">
        </NumberOnlyInput>
        <MultiSearchDropDown
            :width="260"
            placeholder="Select or Search...."
            :clearable="!dim.readOnly"
            :allowCustomValue="false"
            :selectPrimitiveValue="true"
            :disableFilter="false"
            v-model:validation-fail="dim.validation.packaging"
            v-model="dim.packaging"
            :disable="dim.readOnly"
            :data-source="packaging">
        </MultiSearchDropDown>
        <div v-if="dim.showAddButton && !dim.isEditing">
          <Button type="toolbar" text="Add" @on-click="addDimension(dim, index)"></Button>
        </div>
        <div v-if="dim.readOnly && !dim.isDeleting">
          <Button type="info" text="Edit" @on-click="editDimension(dim)"></Button>
        </div>
        <div v-if="dim.isDeleting">
          <Button type="success" text="Yes" @on-click="removeDimension(index, dim)"></Button>
        </div>
        <div v-if="!dim.readOnly && dim.isEditing && !dim.isDeleting">
          <Button type="success" text="Accept" @on-click="acceptEdit(dim, index)"></Button>
        </div>
        <div v-if="!dim.showAddButton && !dim.isDeleting">
          <Button type="danger" text="Delete" @on-click="prepareDelete(dim)"></Button>
        </div>
        <div v-if="dim.isDeleting">
          <Button type="secondary" text="No" @on-click="cancelDelete(dim)"></Button>
        </div>
      </div>
    </div>
    <div class="cmp-dimentions-entry_validation-error" v-if="!props.validationFail">Please Enter Required Dimensions
    </div>
  </div>

</template>


<style lang="scss" scoped>

.cmp-dimentions-entry {

  &_wrapper {
    width: 940px;
    display: flex;
    gap: 4px;
  }

  &_wrapper > div {
    margin-bottom: 3px;
  }

  &_container {
    height: 145px;
    overflow-y: auto;
    overflow-x: hidden;
  }

  &_dims-header {
    margin-bottom: 3px;
    font-size: 13px;
    display: grid;
    grid-template-columns: 62px 104px  104px 104px 103px 262px;

  }

  &_dims-header div {
    //background-color: #ffffff;
    //padding: 5px 5px 5px 5px;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    //border-left: 1px solid #b8b8b8;
    color: #2b2b2b;
    font-size: 13px;
    font-weight: bold;
    display: flex;

  }

  &_validation-error {
    color: var(--required-field);
    font-size: 12px;
  }

}

</style>
